import { useEffect, useRef, useState } from "react";
import { Link } from "@/lib/router";
import Markdown from "react-markdown";
import { ArrowUp, ChevronDown, MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api/http";

type CitedNote = {
  id: string;
  sermon_date: string | null;
  title: string | null;
};

type ChatEntry = {
  role: "user" | "assistant";
  content: string;
  citedNotes?: CitedNote[];
};

/**
 * 챗봇이 "제공하는 기능" 메뉴. 칩은 항상 화면에 남아 있고, 누르면 정해진
 * 사용법 안내를 그대로 보여준다(서버 호출 없음, 매번 같은 출력). 지금은
 * 노트 검색 하나뿐이지만, 기능이 늘면 여기에 항목만 추가하면 된다 —
 * 안 만든 기능을 메뉴에 미리 넣지 않는다.
 */
const FEATURES = [
  {
    label: "노트 검색 사용법",
    guide: [
      "**노트 검색 사용법**",
      "1. 찾고 싶은 상황이나 주제를 입력창에 적어주세요.\n예) 요즘 인간관계가 힘들 때, 감사, 이웃 사랑",
      "2. 한 단어보다 '이웃 사랑에 대한 노트'처럼 주제를 함께 적으면 더 잘 찾아요.",
      "3. 결과로 나온 노트를 누르면 상세 화면으로 이동해요.",
      "내가 기록한 노트 안에서만 찾고, 설명이나 조언은 하지 않아요.",
    ].join("\n\n"),
  },
];

const GREETING =
  "안녕하세요! 내가 기록한 설교 노트에서 필요한 내용을 찾아드려요. 상황이나 단어를 입력하거나, 아래에서 사용법을 확인해보세요.";

/**
 * 우측 하단 플로팅 챗. "내 노트 안에서만" 찾아주는 노트 검색(F5) —
 * 답변 문장은 만들지 않고, 찾은 노트 카드(클릭 시 상세로 이동)만 보여준다.
 *
 * AppShell 안에 마운트되므로 (app-pages) 안에서 페이지를 이동해도
 * 대화 내용이 유지된다. 새로고침하면 사라진다(chat_messages에는 서버가
 * 저장하지만 지난 대화 불러오기는 아직 없다).
 *
 * 다크 테마에서 card(#141517)와 background(#0b0b0c)가 거의 같은 색이라
 * 패널이 배경에 묻힌다 — 테두리 링과 큰 그림자로 띄우고, 말풍선은
 * 사용자=밝은색(primary)/안내·결과=한 단계 밝은 회색(accent)으로 대비를 준다.
 */
export function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [entries, loading]);

  function showGuide(feature: (typeof FEATURES)[number]) {
    setError(null);
    setEntries((prev) => [
      ...prev,
      { role: "user", content: feature.label },
      { role: "assistant", content: feature.guide },
    ]);
  }

  async function search(rawMessage: string) {
    const message = rawMessage.trim();
    if (!message || loading) return;

    setEntries((prev) => [...prev, { role: "user", content: message }]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error ?? "알 수 없는 오류가 발생했어요.");
        return;
      }

      setEntries((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, citedNotes: data.citedNotes },
      ]);
    } catch {
      setError("네트워크 오류가 발생했어요. 잠시 후 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {open ? (
        <section
          aria-label="내 노트에게 묻기"
          className="fixed bottom-24 right-4 z-40 flex h-[min(40rem,calc(100vh-7rem))] w-[min(28rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-foreground/15 bg-popover shadow-2xl ring-1 ring-black/40 sm:right-6"
        >
          <header className="flex items-center gap-3 border-b border-border px-5 py-4">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <MessageCircle className="size-5" />
            </div>
            <div className="flex flex-col">
              <h2 className="text-base font-semibold leading-tight">내 노트에게 묻기</h2>
              <p className="text-xs text-muted-foreground">내가 기록한 노트 안에서만 찾아드려요</p>
            </div>
            <button
              type="button"
              aria-label="챗 닫기"
              onClick={() => setOpen(false)}
              className="ml-auto flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="size-5" />
            </button>
          </header>

          <div ref={scrollRef} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5">
            <div className="max-w-[88%] self-start rounded-2xl rounded-bl-md bg-accent px-4 py-3 text-[15px] leading-relaxed text-accent-foreground">
              {GREETING}
            </div>

            {entries.map((entry, i) => (
              <div
                key={i}
                className={cn(
                  "flex max-w-[88%] flex-col gap-3 rounded-2xl px-4 py-3 text-[15px] leading-relaxed",
                  entry.role === "user"
                    ? "self-end rounded-br-md bg-primary text-primary-foreground"
                    : "self-start rounded-bl-md bg-accent text-accent-foreground",
                )}
              >
                {entry.role === "assistant" ? (
                  <div className="flex flex-col gap-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5">
                    <Markdown>{entry.content}</Markdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{entry.content}</p>
                )}
                {entry.citedNotes && entry.citedNotes.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {entry.citedNotes.map((n) => (
                      <Link
                        key={n.id}
                        href={`/notes/${n.id}`}
                        className="flex flex-col gap-0.5 rounded-xl border border-foreground/15 bg-popover px-3 py-2.5 hover:border-foreground/40"
                      >
                        <span className="truncate text-sm font-medium text-foreground">
                          {n.title ?? "제목 없음"}
                        </span>
                        {n.sermon_date ? (
                          <span className="text-xs text-muted-foreground">{n.sermon_date}</span>
                        ) : null}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}

            {loading ? (
              <div className="self-start rounded-2xl rounded-bl-md bg-accent px-4 py-3 text-sm text-muted-foreground">
                노트 찾는 중...
              </div>
            ) : null}
            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            {/* 기능 칩 — 대화가 쌓여도 항상 맨 아래에 남아 있고, 줄바꿈되며 전부 보인다 */}
            {!loading ? (
              <div className="flex flex-wrap gap-2">
                {FEATURES.map((f) => (
                  <button
                    key={f.label}
                    type="button"
                    onClick={() => showGuide(f)}
                    className="rounded-full border border-foreground/20 bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              search(input);
            }}
            className="px-4 pb-4"
          >
            <div className="flex items-center gap-2 rounded-2xl border border-foreground/20 bg-background py-2 pl-4 pr-2 focus-within:border-foreground/70 focus-within:ring-2 focus-within:ring-foreground/20">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="상황이나 단어를 적어보세요"
                disabled={loading}
                className="h-9 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground disabled:opacity-50"
              />
              <button
                type="submit"
                aria-label="검색"
                disabled={loading || !input.trim()}
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:opacity-30"
              >
                <ArrowUp className="size-5" />
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        aria-label={open ? "챗 닫기" : "내 노트에게 묻기"}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-4 right-4 z-40 flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-2xl ring-1 ring-black/40 transition-transform hover:scale-105 sm:right-6"
      >
        {open ? <ChevronDown className="size-6" /> : <MessageCircle className="size-6" />}
      </button>
    </>
  );
}
